<template>
  <div>
    <UnavailableCountry
      class="my-24"
      :country="shippingAddress?.country || null"
      :shippable-countries
    />
    <ShippingAddressForm
      :address="formValues"
      :api-errors
      :countries
      :country="countryCode"
      :submitting="submitting || loading"
      :user-information
      :with-same-billing-checkbox
      :with-user-information="userInformationStore.isFormRequired"
      @submit="handleShippingAddressSubmit"
      @submit-failed="handleShippingAddressSubmitFailure"
    />

    <GDPRNotice page="delivery" />
    <ReassuranceItems class="mt-24 md:mt-56" :selected-mobile-plan />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { BuybackOffer, Country } from '@backmarket/http-api'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/composables/buybackOffer/useBuybackOffer'
import type {
  Deliver,
  UserInformation,
} from '@backmarket/nuxt-layer-checkout/api/cart/types'
import { useAddressStore } from '@backmarket/nuxt-layer-checkout/stores/addressStore'
import { useUserInformationStore } from '@backmarket/nuxt-layer-checkout/stores/userInformationStore'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useLogUnhandledAPIErrors } from '@backmarket/nuxt-module-address/useLogUnhandledAPIErrors'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import {
  getPhoneNumberInfos,
  getPhoneNumberToE164,
} from '@ds/components/InputPhone'

import { getSelectedMobilePlan } from '~/scopes/checkout/utils/getSelectedMobilePlan'

import useHandleUnauthorizedUser from '../../composables/useHandleUnauthorizedUser'
import { useSaveBouyguesAddress } from '../../composables/useSaveBouyguesAddress'
import { getAPIErrors } from '../../utils/getAPIErrors'
import GDPRNotice from '../GDPRNotice/GDPRNotice.vue'
import ReassuranceItems from '../ReassuranceItems/ReassuranceItems.vue'

import ShippingAddressForm from './components/ShippingAddressForm/ShippingAddressForm.vue'
import UnavailableCountry from './components/UnavailableCountry/UnavailableCountry.vue'

const submitting = ref(false)
const apiErrors = ref({})

const logger = useLogger()
const tracking = useTracking()
const $httpFetch = useHttpFetch()

const {
  market: { countryCode },
} = useMarketplace()
const { user } = useUserStore()

const addressStore = useAddressStore()
const userInformationStore = useUserInformationStore()

const { openErrorToast } = useTheToast()
const { logUnhandledAPIErrors } = useLogUnhandledAPIErrors()
const { handleUnauthorizedUser } = useHandleUnauthorizedUser()
const { saveBouyguesAddress } = useSaveBouyguesAddress()
const { resetBuybackOffer } = useBuybackOffer()

const props = defineProps<{
  loading: boolean
  items: ApiSpecs['/bm/cart']['GET']['response']['cartItems']
  shippingAddress: ApiSpecs['/bm/cart']['GET']['response']['addressList']['deliver']
  shippableCountries: ApiSpecs['/bm/cart']['GET']['response']['shippableCountries']
  swapOffer: BuybackOffer | null
  withSameBillingCheckbox?: boolean
}>()

const emit = defineEmits<{
  (e: 'update', sameAsShipping: boolean): void
}>()

const selectedMobilePlan = computed(() => {
  return getSelectedMobilePlan(props.items)
})

const address = computed(() => {
  const defaultAddress: Deliver = {
    birthdate: null,
    city: null,
    company: null,
    country: null,
    countryDialInCode: null,
    customerIdNumber: null,
    dateCreation: null,
    dateModification: null,
    email: null,
    firstName: null,
    firstNamePronunciation: null,
    lastName: null,
    lastNamePronunciation: null,
    phone: null,
    postalCode: null,
    stateOrProvince: null,
    street: null,
    street2: null,
    warnings: [],
  }

  if (!props.shippingAddress) {
    return defaultAddress
  }

  if (
    props.shippingAddress.country &&
    !props.shippableCountries.some(
      (country) => country.countryCode === props.shippingAddress?.country,
    )
  ) {
    return {
      ...defaultAddress,
      country: countryCode,
    }
  }

  return props.shippingAddress
})

const formValues = computed(() => {
  const phone = `${address.value.phone || user.value.phone || ''}`
  const countryDialInCode = `${address.value.countryDialInCode || ''}`

  logger.debug('[Checkout] phone number parsing: ', {
    owners: ['bot-squad-checkout-front'],
    phone:
      phone && countryDialInCode
        ? getPhoneNumberToE164(countryDialInCode, phone)
        : phone,
  })

  return {
    ...address.value,
    // @TODO need to fix this type conversion
    country:
      (address.value.country as Country) ||
      (countryCode as unknown as Country) ||
      '',
    firstName: address.value.firstName || user.value.firstName || '',
    lastName: address.value.lastName || user.value.lastName || '',
    phone:
      phone && countryDialInCode
        ? getPhoneNumberToE164(countryDialInCode, phone)
        : phone,
  } as Deliver
})

const userInformation = computed<Partial<UserInformation>>(() => ({
  birthdate: userInformationStore.birthdate,
  taxId: userInformationStore.nationalId ?? undefined,
}))

const countries = computed(() => {
  return [...props.shippableCountries]
    .map(({ name, countryCode: shippableCountryCode }) => ({
      value: shippableCountryCode,
      label: name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

const handleShippingAddressSubmitFailure = (errors: Error) => {
  logger.info('[CHECKOUT] Failed to submit shipping form', {
    errors: { fields: Object.keys(errors) },
    owners: ['bot-squad-checkout-front'],
  })
  tracking.trackClick({
    name: 'shipping_address_saved_failure',
    zone: 'address',
  })
}

const handleShippingAddressSubmit = async (
  values: Deliver,
  sameAsShipping: boolean,
  userInformationValues: Partial<UserInformation>,
) => {
  apiErrors.value = {}
  submitting.value = true

  try {
    // values.phone is mandatory and cannot by null
    const { dial, nationalNumber } = getPhoneNumberInfos(
      values.phone ?? '',
      values.country as Country,
    )

    const shippingAddress = {
      ...values,
      phone: nationalNumber,
      countryDialInCode: dial,
    }

    if (isEmpty(nationalNumber)) {
      logger.debug('[CHECKOUT] Failed to convert phone in shipping form', {
        phone: values.phone,
        owners: ['bot-squad-checkout-front'],
      })
    }

    if (props.swapOffer) {
      try {
        await addressStore.createBuybackAddress({
          ...shippingAddress,
        })
      } catch {
        // Remove the swap offer and proceed to payment -> CK-5631
        await $httpFetch('/bm/cart/swap', { method: 'DELETE' })
        resetBuybackOffer()
      }
    }

    if (selectedMobilePlan.value?.type === 'ONLINE') {
      await saveBouyguesAddress(
        shippingAddress,
        sameAsShipping ? 'BILLING_AND_SHIPPING' : 'SHIPPING',
      )
    }

    await addressStore.saveAddress({
      isShipping: true,
      isBilling: sameAsShipping,
      ...insertIf(userInformationStore.isFormRequired, {
        formType: userInformationStore.formType,
      }),
      address: shippingAddress,
      userInformation: userInformationValues,
    })

    tracking.trackClick({
      name: 'shipping_address_saved',
      zone: 'address',
    })

    emit('update', sameAsShipping)
    submitting.value = false
  } catch (e) {
    const error = e as Record<string, unknown>

    tracking.trackClick({
      name: 'shipping_address_saved_failure',
      zone: 'address',
    })

    if (error?.status === 400 || error?.status === 422) {
      const errors = getAPIErrors(error)
      logUnhandledAPIErrors(Object.keys(errors))
      apiErrors.value = errors
      openErrorToast()
    } else
      await handleUnauthorizedUser(
        error,
        '[CHECKOUT] Failed to submit shipping form',
      )
    submitting.value = false
  }
}
</script>
